import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { COUNTRY_CODES } from "../../../../constants/countryCodes";
import "./CreateUser.css";

export default function EditBaseUser({ showEdit, closeModal, refreshPage, base_user }) {
  const [validationError, setValidationError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const countryCodes = COUNTRY_CODES;

  const [formData, setFormData] = useState({
    id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country_code: "+234",
    status: "",
  });

  // Prepopulate form with base_user data
  useEffect(() => {
    if (base_user) {
      const phone = base_user.phone || "";
      let country_code = "+1"; // Default to USA
      let phone_number = phone;

      // Find matching country code
      const matchedCountry = countryCodes.find((country) =>
        phone.startsWith(country.code)
      );
      if (matchedCountry) {
        country_code = matchedCountry.code;
        phone_number = phone.slice(country_code.length);
      } else if (phone.startsWith("+")) {
        const possibleCode = phone.match(/^\+\d{1,4}/)?.[0];
        if (possibleCode) {
          country_code = possibleCode;
          phone_number = phone.slice(possibleCode.length);
        }
      }

      setFormData({
        id: base_user.base_user_id || "",
        first_name: base_user.first_name || "",
        last_name: base_user.last_name || "",
        email: base_user.email || "",
        phone: phone_number,
        country_code,
        status: base_user.status || "",
      });
    }
  }, [base_user, countryCodes]);

  const resetForm = () => {
    setFormData({
      id: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      country_code: "+234", 
      status: "",
    });
    setValidationError({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Allow only numbers for phone
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({ ...prev, phone: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        id: formData.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: `${formData.country_code}${formData.phone}`,
        status: formData.status,
      };

      const response = await axiosInstance.post(`/user/base_user/edit_base_user`, payload);

      if (response.data.success) {
        toast.success("Base user updated successfully!");
        refreshPage();
        handleClose();
      } else {
        toast.error(response.data.message || "Failed to update user.");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      if (error.response && error.response.data.errors) {
        setValidationError(error.response.data.errors);
      }
      toast.error(
        error.response?.data?.message ||
          "An error occurred while updating the user."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="CreateUser">
      <Modal show={showEdit} onHide={handleClose}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit User Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formFirstName">
              <div>
                <h6>First Name</h6>
              </div>
              <Form.Control
                type="text"
                placeholder="Enter..."
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
              {validationError.first_name && (
                <small className="text-danger my-1">
                  {validationError.first_name[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formLastName">
              <div>
                <h6>Last Name</h6>
              </div>
              <Form.Control
                type="text"
                placeholder="Enter..."
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
              {validationError.last_name && (
                <small className="text-danger my-1">
                  {validationError.last_name[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmail">
              <div>
                <h6>Email</h6>
              </div>
              <Form.Control
                type="email"
                placeholder="Enter..."
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {validationError.email && (
                <small className="text-danger my-1">
                  {validationError.email[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPhone">
              <div>
                <h6>Phone</h6>
              </div>
              <div className="input-group">
                <select
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleChange}
                  className="form-control"
                  style={{ width: "40%" }}
                >
                  {countryCodes.length === 0 ? (
                    <option>Loading...</option>
                  ) : (
                    countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))
                  )}
                </select>
                <Form.Control
                  type="tel"
                  placeholder="Enter phone number..."
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-control"
                  style={{ width: "60%" }}
                />
              </div>
              {validationError.phone && (
                <small className="text-danger my-1">
                  {validationError.phone[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formStatus">
              <div>
                <h6>Status</h6>
              </div>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Status --</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
              {validationError.status && (
                <small className="text-danger my-1">
                  {validationError.status[0]}
                </small>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              style={{ backgroundColor: "#c4281b", color: "#fff" }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}